import { CrudForm } from '../shared/CrudPage';
import { enumEntries, enumValue, licenseService } from '@kss/api';
import type { License, LicenseCreate, LicenseFilter } from '@kss/api';
import { loadSubscriptionPlanOptions } from '../shared/formLoaders';
import { validateDate, validateDateRange } from '../../utils/dateValidation';

type F = {
  planId: string;
  startDate: string;
  endDate: string;
  status: string;
  maxUsers: number;
  autoRenew: boolean;
  isActive: boolean;
};
const today = new Date().toISOString().slice(0, 10);
const empty: F = {
  planId: '',
  startDate: today,
  endDate: today,
  status: 'Trial',
  maxUsers: 5,
  autoRenew: false,
  isActive: true,
};

export default function NewLicense() {
  return (
    <CrudForm<License, LicenseCreate, LicenseFilter, F>
      title="license"
      service={licenseService}
      idKey="licenseId"
      basePath="/panel/licenses"
      emptyForm={empty}
      fields={[
        // Plan picker — async loader hits subscriptionPlanService.all()
        // and surfaces options as { name (label), description / price }.
        // SearchableSelect is now eager-loading so the saved plan name
        // appears immediately in edit mode.
        {
          key: 'planId',
          label: 'Plan',
          type: 'search',
          required: true,
          loadOptions: loadSubscriptionPlanOptions,
          placeholder: 'Pick a subscription plan',
        },
        { key: 'startDate', label: 'Start date', type: 'date' },
        { key: 'endDate', label: 'End date', type: 'date' },
        {
          key: 'status',
          label: 'Status',
          type: 'select',
          /* `status` is an int column. This used to send the label STRING
             ('Active', 'Trial', …), so AllLicenses' `Number(r.status)` gave
             NaN and the grid rendered "License Status #NaN". Options now
             carry the int value from the shared registry, and 'Cancelled'
             — previously unreachable — is included. */
          options: enumEntries('LicenseStatus').map((e) => ({
            value: String(e.value),
            label: e.label,
          })),
        },
        { key: 'maxUsers', label: 'Max users', type: 'number' },
        { key: 'autoRenew', label: 'Auto renew', type: 'status' },
        { key: 'isActive', label: 'Active', type: 'status' },
      ]}
      fromEntity={(r) => ({
        // License accepts both `subscriptionPlanId` (canonical, live API)
        // and `planId` (deprecated). Prefer the canonical one.
        planId: r.subscriptionPlanId ?? r.planId ?? '',
        startDate: (r.startsAt ?? r.startDate)?.slice(0, 10) ?? today,
        endDate: (r.expiresAt ?? r.endDate)?.slice(0, 10) ?? today,
        /* Rows written before the string→int fix still hold labels, so
           coerce those back through the registry; genuine ints pass through. */
        status: String(
          typeof r.status === 'number'
            ? r.status
            : enumValue('LicenseStatus', r.status as string | undefined) ?? 2,
        ),
        maxUsers: Number(r.maxUsers ?? 5),
        autoRenew: !!r.autoRenew,
        isActive: !!r.isActive,
      })}
      toEntity={(f, id) => ({
        licenseId: id,
        // Live API (verified 2026-05-05) uses the canonical names
        // `subscriptionPlanId`, `startsAt`, `expiresAt`. The strict-mode
        // binder now rejects the legacy `planId` / `startDate` / `endDate`
        // keys — only the canonical set is on the wire.
        subscriptionPlanId: f.planId || undefined,
        startsAt: f.startDate || undefined,
        expiresAt: f.endDate || undefined,
        status: f.status ? Number(f.status) : undefined,
        maxUsers: f.maxUsers || undefined,
        autoRenew: f.autoRenew,
        isActive: f.isActive,
      })}
      validate={(f) => {
        if (!f.planId.trim()) return 'Plan is required';
        const startErr = validateDate(f.startDate, 'Start date');
        if (startErr) return startErr;
        const endErr = validateDate(f.endDate, 'End date');
        if (endErr) return endErr;
        const rangeErr = validateDateRange(f.startDate, f.endDate, 'Start date', 'End date');
        if (rangeErr) return rangeErr;
        return null;
      }}
    />
  );
}
